import { useMemo } from 'react';

import { SpendChartCard } from '@apps/web/components/admin/llm_spend/chart_shared';
import { SURFACE_SERIES } from '@apps/web/components/admin/llm_spend/constants';
import { ModelComponentSpendCard } from '@apps/web/components/admin/llm_spend/model_component_spend_card';
import { ProviderSpendCard } from '@apps/web/components/admin/llm_spend/provider_spend_card';
import {
    combinedCoverage,
    coverageNote,
    formatProviderTotals,
} from '@apps/web/components/admin/llm_spend/provider_spend_math';
import { SpendLineChart } from '@apps/web/components/admin/llm_spend/spend_line_chart';
import {
    dimensionSeriesFor,
    foldDimensionSpend,
    formatUsd,
    formatUsdCompact,
    percentChange,
    ratio,
    spendByModel,
} from '@apps/web/components/admin/llm_spend/spend_math';
import { SpendTotalsTable } from '@apps/web/components/admin/llm_spend/spend_totals_table';
import { StackedSpendCard } from '@apps/web/components/admin/llm_spend/stacked_spend_chart';
import { type SpendTabProps } from '@apps/web/components/admin/llm_spend/tab_types';
import { KpiStatCard } from '@apps/web/components/admin/outreach_observability/kpi_stat_card';

export function OverviewTab({
    data,
    bucket,
    comparisonLabel,
}: SpendTabProps & {
    // Null when the range has no prior period to compare against.
    readonly comparisonLabel: string | null;
}) {
    const modelSpend = useMemo(
        () =>
            data.buckets.map((row) =>
                spendByModel(row.spend_by_model_component),
            ),
        [data.buckets],
    );
    const modelSeries = useMemo(
        () => dimensionSeriesFor(modelSpend),
        [modelSpend],
    );
    const modelPoints = data.buckets.map((row, index) => ({
        bucketStart: row.bucket_start,
        values: foldDimensionSpend(modelSpend[index] ?? {}, modelSeries),
    }));
    const surfacePoints = data.buckets.map((row) => ({
        bucketStart: row.bucket_start,
        values: foldDimensionSpend(row.spend_by_surface, SURFACE_SERIES),
    }));
    const perTrackedPoints = data.buckets.map((row) => ({
        bucketStart: row.bucket_start,
        values: {
            per_organization: ratio(
                row.tracked_estimated_spend_usd,
                row.tracked_organization_count,
            ),
            per_user: ratio(
                row.tracked_estimated_spend_usd,
                row.tracked_user_count,
            ),
        },
    }));
    const providers = data.providers?.providers ?? [];
    const providerTotals = data.providers?.totals ?? [];
    let billedSpend = 'Not configured';
    if (providerTotals.length > 0) {
        billedSpend = formatProviderTotals(providerTotals);
    } else if (providers.length > 0) {
        billedSpend = 'Not imported';
    }
    const billedSpendSub =
        coverageNote(combinedCoverage(providers), data) ??
        'Reported by provider billing systems';
    return (
        <div className="flex flex-col gap-4">
            <div className="grid gap-3 md:grid-cols-2">
                <KpiStatCard
                    label="Tracked estimated spend"
                    value={formatUsd(data.totals.tracked_estimated_spend_usd)}
                    sub="Token-priced estimate; not a billed total"
                    deltas={
                        comparisonLabel === null
                            ? []
                            : [
                                  {
                                      label: comparisonLabel,
                                      pct: percentChange(
                                          data.totals
                                              .tracked_estimated_spend_usd,
                                          data.previous_totals
                                              .tracked_estimated_spend_usd,
                                      ),
                                      goodWhenUp: false,
                                  },
                              ]
                    }
                    compact={true}
                />
                <KpiStatCard
                    label="Billed spend"
                    value={billedSpend}
                    sub={billedSpendSub}
                    deltas={[]}
                    compact={true}
                />
            </div>
            <div className="grid gap-4 xl:grid-cols-2">
                <StackedSpendCard
                    title="Spend by surface"
                    points={surfacePoints}
                    series={SURFACE_SERIES}
                    bucket={bucket}
                >
                    <SpendTotalsTable
                        dimensionLabel="Surface"
                        points={surfacePoints}
                        series={SURFACE_SERIES}
                    />
                </StackedSpendCard>
                <SpendChartCard title="Spend per organization and user with tracked spend">
                    <SpendLineChart
                        points={perTrackedPoints}
                        series={[
                            {
                                key: 'per_organization',
                                label: 'Per tracked org',
                                color: 'var(--spend-series-1)',
                            },
                            {
                                key: 'per_user',
                                label: 'Per tracked user',
                                color: 'var(--spend-series-2)',
                            },
                        ]}
                        bucket={bucket}
                        valueFormatter={formatUsdCompact}
                    />
                </SpendChartCard>
            </div>
            <div className="grid gap-4 xl:grid-cols-2">
                <StackedSpendCard
                    title="Spend by model"
                    points={modelPoints}
                    series={modelSeries}
                    bucket={bucket}
                >
                    <SpendTotalsTable
                        dimensionLabel="Model"
                        points={modelPoints}
                        series={modelSeries}
                    />
                </StackedSpendCard>
                <ModelComponentSpendCard
                    buckets={data.buckets}
                    bucket={bucket}
                />
            </div>
            <ProviderSpendCard section={data.providers} range={data} />
        </div>
    );
}
